import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@shadcn/components/ui/table';
import { Button } from '@shadcn/components/ui/button';
import { Badge } from '@shadcn/components/ui/badge';
import { Input } from '@shadcn/components/ui/input';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger 
} from '@shadcn/components/ui/dropdown-menu';
import { Skeleton } from '@shadcn/components/ui/skeleton';
import { 
  Edit, 
  Trash2, 
  MoreHorizontal, 
  Search,
  ArrowUpDown,
  ArrowUp,
  ArrowDown
} from 'lucide-react';

import { StatusBadge } from '../TicketSystem/StatusBadge';
import { TicketTypeIcon } from '../TicketSystem/TicketTypeIcon';
import { TICKET_TYPE_LABELS, META_STATUS_LABELS } from '../../constants';

/**
 * StatusMappingTable Component
 * 
 * Displays status mappings in a sortable, searchable table format
 */
const StatusMappingTable = ({ 
  mappings = [], 
  loading = false, 
  onEdit, 
  onDelete 
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  /**
   * Handle sorting
   */
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  /**
   * Get sort icon
   */
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown className="w-4 h-4 text-gray-400" />;
    }
    return sortConfig.direction === 'asc' 
      ? <ArrowUp className="w-4 h-4 text-blue-600" />
      : <ArrowDown className="w-4 h-4 text-blue-600" />;
  };

  /**
   * Filter and sort mappings
   */
  const filteredAndSortedMappings = useMemo(() => {
    let filtered = mappings;

    // Apply search filter
    if (searchTerm) {
      filtered = mappings.filter(mapping =>
        mapping.originalStatus.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.metaStatus.toLowerCase().includes(searchTerm.toLowerCase()) ||
        mapping.ticketType.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (mapping.boardId && mapping.boardId.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aValue = a[sortConfig.key];
        let bValue = b[sortConfig.key];

        // Handle nested properties
        if (sortConfig.key.includes('.')) {
          const keys = sortConfig.key.split('.');
          aValue = keys.reduce((obj, key) => obj?.[key], a);
          bValue = keys.reduce((obj, key) => obj?.[key], b);
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [mappings, searchTerm, sortConfig]);

  /**
   * Render loading skeleton
   */
  const renderLoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton className="h-4 w-20" /></TableCell>
          <TableCell><Skeleton className="h-4 w-32" /></TableCell>
          <TableCell><Skeleton className="h-6 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-24" /></TableCell>
          <TableCell><Skeleton className="h-4 w-16" /></TableCell>
        </TableRow>
      ))}
    </>
  );

  /**
   * Render empty state
   */
  const renderEmptyState = () => (
    <TableRow>
      <TableCell colSpan={5} className="text-center py-8">
        <div className="flex flex-col items-center gap-2 text-muted-foreground">
          <Search className="w-8 h-8" />
          <p>No status mappings found</p>
          {searchTerm && (
            <p className="text-sm">
              Try adjusting your search term: "{searchTerm}"
            </p>
          )}
        </div>
      </TableCell>
    </TableRow>
  );

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="flex items-center gap-2">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder="Search mappings..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>
        
        {searchTerm && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setSearchTerm('')}
          >
            Clear
          </Button>
        )}
        
        <div className="text-sm text-muted-foreground">
          {filteredAndSortedMappings.length} of {mappings.length} mappings
        </div>
      </div>

      {/* Table */}
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('ticketType')}
              >
                <div className="flex items-center gap-2">
                  Ticket Type
                  {getSortIcon('ticketType')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('originalStatus')}
              >
                <div className="flex items-center gap-2">
                  Original Status
                  {getSortIcon('originalStatus')}
                </div>
              </TableHead>
              
              <TableHead 
                className="cursor-pointer hover:bg-gray-50"
                onClick={() => handleSort('metaStatus')}
              >
                <div className="flex items-center gap-2">
                  Meta Status
                  {getSortIcon('metaStatus')}
                </div>
              </TableHead>
              
              
              
              <TableHead className="w-[100px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          
          <TableBody>
            {loading ? (
              renderLoadingSkeleton()
            ) : filteredAndSortedMappings.length === 0 ? (
              renderEmptyState()
            ) : (
              filteredAndSortedMappings.map((mapping) => (
                <TableRow key={mapping.id} className="hover:bg-gray-50">
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <TicketTypeIcon 
                        type={mapping.ticketType} 
                        size="sm" 
                        showBackground={false}
                      />
                      <span className="font-medium">
                        {TICKET_TYPE_LABELS[mapping.ticketType]}
                      </span>
                    </div>
                  </TableCell>
                  
                  <TableCell>
                    <code className="bg-gray-100 px-2 py-1 rounded text-sm">
                      {mapping.originalStatus}
                    </code>
                  </TableCell>
                  
                  <TableCell>
                    <StatusBadge 
                      metaStatus={mapping.metaStatus}
                      showOriginal={false}
                    />
                  </TableCell>
                  
                  
                  
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem
                          onClick={() => onEdit?.(mapping)}
                          className="cursor-pointer"
                        >
                          <Edit className="w-4 h-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          onClick={() => onDelete?.(mapping.id)}
                          className="cursor-pointer text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

export default StatusMappingTable;
